import { useEffect, useRef, useState } from 'react';

import * as levenshtein from 'fast-levenshtein';
import { Helmet } from 'react-helmet';
import { useParams, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Flex, Box } from 'reflexbox';

import TransmitIcon from 'assets/img/icons/wifi_icon.svg';
import {
  Loader, ModalWindow, Select, Button, ActionButton, Input, Checkbox, RouterPrompt,
} from 'components';
import { getCachedDevInfo, getCachedDevInfoSync } from 'helpers/cachedStorage';
import { getUserProfile } from 'helpers/userProfile';
import { useStateVar } from 'helpers/useStateVar';
import { useWebSocket, WSConn } from 'helpers/wsConnection';
import {
  DeleteOutlineIcon,
  EditIcon,
} from 'icons';
import { DevLayout } from 'pages/Analysis/DEVs/DevLayout';
import { apiCall } from 'providers';
import { colors } from 'styles/colors';
import { AssetLayout } from 'pages/Analysis/Assets/AssetLayout';

import {
  Card,
  IrTableContainer,
  IrTableHead,
  IrTableBody,
  IrHeaderCell,
  IrDataCell,
  IrRow,
  SelectContainer,
  CustomSelect,
  TextLine,
  Overlay,
  TempBadge,
  FanBadge,
  OffBadge,
} from './styles';
import { useTranslation } from 'react-i18next';
import { MdArrowDropDown, MdArrowRight } from 'react-icons/md';
import { withTransaction } from '@elastic/apm-rum-react';
import { generateNameFormatted } from '~/helpers/titleHelper';
import { DevFullInfo } from '~/store';
import { isFwVersionGreatestOrEqual } from '~/helpers';

function getDutDefaultTemps() {
  const temps = [] as { name: string, value: number }[];
  for (let index = 16; index <= 30; index += 1) {
    temps.push({ name: `${index}°C`, value: index });
  }
  return temps;
}

interface ModalCmdType {
  CMD_TYPE: { name: string; value: string } | null;
  TEMPER: { name: string; value: number } | null;
  CMD_DESC: string | null;
}

export interface DutCmdResponse {
  message: {
    msgtype: 'command_response';
    response: 'set_ir_learning';
    status_code: number;
    dev_id: string;
    result?: any;
    error?: {
      esp_code: number;
      msg: string;
    };
  }
}

export const DutIrManagement = (): JSX.Element => {
  const { t } = useTranslation();
  const history = useHistory();
  const routeParams = useParams<{ devId: string }>();
  const [profile] = useState(getUserProfile);
  const irLearningTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [state, render, setState] = useStateVar(() => ({
    modify: false,
    haveError: false,
    devId: routeParams.devId,
    loading: true,
    savingCmdType: false,
    savingCmdsTable: false,
    savingFakeCmds: false,
    deletingSelectedCmds: false,
    transmittingCommand: false,
    selectedDutCommands: 0,
    selectPositionY: 0,
    assetLayout: false,
    selectPositionX: 0,
    devInfo: getCachedDevInfoSync(routeParams.devId),
    defaultIrList: [] as { IR_ID: string, CMD_TYPE: string, CMD_DESC: string, TEMPER: number }[],
    irList: [] as { IR_ID: string, CMD_TYPE: string, CMD_DESC: string, TEMPER: number }[],
    loadedIrList: [] as {
      IR_ID: string;
      CMD_TYPE: string;
      CMD_DESC: string;
      TEMPER: number;
    }[],
    dutCommandTypes: [{ name: t('refrigerar'), value: 'COOL' }, { name: t('desligar'), value: 'OFF' }, { name: t('ventilar'), value: 'FAN' }, { name: t('outro'), value: 'OTHER' }],
    dutDefaultTemps: getDutDefaultTemps(),
    dutCommandActions: [] as string[],
    probableIrCommands: null as null | {
      DUT_ID: string;
      MCHN_MODEL: string;
      MCHN_BRAND: string;
      model: string;
      distance: number;
      dropdown_text: string;
    }[],
    loadedIrModel: '',
    checkedAllCodes: false,
    lastIr: {
      id: null,
      ts: null,
      isOld: null,
      timer: null,
    },
    showModal: null,
    modalCmdType: {
      IR_ID: null,
      CMD_TYPE: null as null | { name: string, value: string },
      CMD_DESC: null as null | string,
      CMD_ACTION: null as null | string,
      TEMPER: null as null | { name: string, value: number },
    },
    wsConn: null as null | WSConn,
    learnActive: false,
    learnActiveAwait: false,
    learnActiveBox: false,
    rends: 0,
    comboOpts: {} as {
      units?: { label: string, value: number }[],
      groups?: { label: string, value: number, unit: number }[],
      fluids?: { label: string, value: string }[],
      applics?: { label: string, value: string }[],
      types?: { label: string, value: string }[],
      envs?: { label: string, value: string }[],
      brands?: { label: string, value: string }[],
      roles?: { label: string, value: string }[],
      psens?: { label: string, value: string }[],
      vavs?: { label: string, value: string }[],
      rtypes?: { RTYPE_NAME: string, RTYPE_ID: number }[],
      dutPlacement?: { label: string, value: string }[],
    },
    MCHN_BRAND_item: null as null|{ label: string, value: string },
    MCHN_MODEL: '',
    openEdit: false,
    savingModel: false,
    permissionGeral: false,
    isLoadingIrLearning: false, // Overlay de loading específico para aprendizagem de IR quando o usuário selecionar ou desselecionar a opção de habilitar aprendizagem.
  }));

  function setMachineBrandModelForm(devInfo: DevFullInfo): void {
    if (!profile.permissions.isInstaller) return;

    if (!devInfo.dut_aut) return;

    const dutAut = devInfo.dut_aut;

    setState({
      MCHN_BRAND_item: dutAut.MCHN_BRAND ? state.comboOpts.brands?.find((item) => item.value === dutAut.MCHN_BRAND.toLowerCase()) ?? null : null,
      MCHN_MODEL: dutAut.MCHN_MODEL ?? '',
    });
  }

  useEffect(() => {
    Promise.resolve().then(async () => {
      try {
        const [
          devInfo,
          { list, dutCommandTypes },
        ] = await Promise.all([
          getCachedDevInfo(state.devId, {}),
          apiCall('/get-dut-ircodes-list', { devId: state.devId }),
        ]);
        state.devInfo = devInfo;

        const permission = verifyPermissions(devInfo);
        state.permissionGeral = permission;

        // @ts-ignore
        state.irList = list || [];
        state.defaultIrList = state.irList;
        state.loadedIrList = state.irList;
        if (!sessionStorage.getItem('savingOnDut')) {
          sessionStorage.setItem('savingOnDut', JSON.stringify(state.loadedIrList));
        }
        state.dutCommandActions = dutCommandTypes || [];
        if (permission) {
          const probableCommands = await fetchIRCommandSets(state.devId);
          setState({ probableIrCommands: probableCommands });
        }

        setMachineBrandModelForm(devInfo);
      } catch (err) { toast.error(t('erro')); console.error(err); }
      state.loading = false;
      render();
    });
    if (profile.permissions.isInstaller) {
      const reqCombos = {
        fluids: true,
        applics: true,
        types: true,
        envs: true,
        brands: true,
        psens: true,
        ecoModeCfg: true,
        scheduleStartBehavior: true,
        dutScheduleStartBehavior: true,
        dutScheduleEndBehavior: true,
        dutForcedBehavior: true,
      };
      apiCall('/dev/dev-info-combo-options', reqCombos).then((response) => {
        state.comboOpts = response;
      });
    }
  }, [state.savingModel]);

  // Firmwares legados
  useEffect(() => {
    if (!state.wsConn) return;
    if (state.learnActive) {
      state.wsConn.send({
        type: 'dutSubscribeIrRead', data: { DUT_ID: state.devId },
      });
    } else {
      state.wsConn.send({
        type: 'dutUnsubscribeIrRead',
      });
    }
  }, [state.learnActive]);

  const linkBase = history.location.pathname;

  state.assetLayout = linkBase.includes('/ativo');
  useWebSocket(onWsOpen, onWsMessage, beforeWsClose);
  function onWsOpen(wsConn) {
    state.wsConn = wsConn;
    if (state.learnActive) {
      wsConn.send({ type: 'dutSubscribeIrRead', data: { DUT_ID: state.devId } });
    }
    if (state.learnActiveAwait) {
      wsConn.send({ type: 'dutSubscribeIrReadAndWait', data: { DUT_ID: state.devId, enabled: true } });
    }
  }
  function onWsMessage(payload) {
    const { type: msgType, data: msgData } = (payload || {});
    if (msgType === 'dutSubscribeIrReadAndWait') {
      const msg = msgData as DutCmdResponse;
      if (irLearningTimeoutRef.current) {
        clearTimeout(irLearningTimeoutRef.current);
        irLearningTimeoutRef.current = null;
      }
      if (msg.message.response === 'set_ir_learning' && msg.message.status_code === 200 && msg.message.dev_id === state.devId) {
        setState({
          isLoadingIrLearning: false,
          learnActiveBox: state.learnActiveAwait,
        });
        toast.success(state.learnActiveAwait ? t('aprendizagemHabilitada') : t('aprendizagemDesabilitada'));
      } else {
        setState({ learnActiveAwait: false, isLoadingIrLearning: false, learnActiveBox: false });
        toast.error(t('erroNaComunicacaoComODispositivo'));
      }
    }
    if (msgType === 'dutIrLearningTimeout') {
      if (msgData.devId !== state.devId) return;
      if (irLearningTimeoutRef.current) {
        clearTimeout(irLearningTimeoutRef.current);
        irLearningTimeoutRef.current = null;
      }
      setState({
        learnActive: false,
        learnActiveAwait: false,
        learnActiveBox: false,
        isLoadingIrLearning: false,
      });
      toast.warn(t('aprendizagemDesabilitadaPorTimeout'));
    }
    if (msgType === 'dutIrRead') {
      if (msgData.devId !== state.devId) return;
      if (!msgData.IR_ID) return;
      state.lastIr.id = msgData.IR_ID;
      // @ts-ignore
      state.lastIr.ts = Date.now();
      // @ts-ignore
      state.lastIr.isOld = false;
      // @ts-ignore
      clearTimeout(state.lastIr.timer);
      // @ts-ignore
      state.lastIr.timer = setTimeout(() => {
        // @ts-ignore
        state.lastIr.isOld = ((state.lastIr.ts + 1000) < Date.now());
        // @ts-ignore
        if (state.lastIr.isOld) render();
      }, 1500);
      // @ts-ignore
      if (!state.irList.some((x) => x.IR_ID === msgData.IR_ID)) {
        // @ts-ignore
        state.irList.push({
          // @ts-ignore
          IR_ID: msgData.IR_ID,
          // @ts-ignore
          CMD_TYPE: (msgData.CMD_TYPE || null),
          // @ts-ignore
          CMD_DESC: (msgData.CMD_DESC || null),
          // @ts-ignore
          TEMPER: (msgData.TEMPER == null) ? null : msgData.TEMPER,
        });
      }
      render();
    }
  }
  function beforeWsClose(wsConn) {
    wsConn.send({ type: 'dutUnsubscribeIrRead' });
  }

  function isDut4(): boolean {
    return !!(state.devInfo?.dut) && isFwVersionGreatestOrEqual('7.0.0', state.devInfo.FW_VERSION);
  }

  function buildCmdDesc(modalCmdType: ModalCmdType): string {
    const cmdType = modalCmdType.CMD_TYPE?.value;

    if (!cmdType) return '';

    if (cmdType === 'FAN') return 'Fan';
    if (cmdType === 'OFF') return 'Off';

    if (cmdType === 'COOL') {
      const temp = modalCmdType.TEMPER?.value;
      return temp ? `ON_${temp}` : '';
    }

    if (cmdType === 'OTHER') {
      return modalCmdType.CMD_DESC ?? '';
    }

    return cmdType;
  }

  const confirmSetCmdType = async () => {
    try {
      const { IR_ID } = state.modalCmdType;
      const CMD_DESC = buildCmdDesc(state.modalCmdType);

      let CMD_TYPE: string | null = state.modalCmdType.CMD_ACTION || null;
      let TEMPER: number | null = null;

      if (isDut4()) {
        const displayType = state.modalCmdType.CMD_TYPE?.value;
        if (displayType === 'COOL') {
          CMD_TYPE = 'AC_COOL';
          TEMPER = state.modalCmdType.TEMPER?.value ?? null;
        } else if (displayType === 'FAN') {
          CMD_TYPE = 'AC_FAN';
        } else if (displayType === 'OFF') {
          CMD_TYPE = 'AC_OFF';
        } else if (displayType === 'OTHER') {
          const match = /^ON_(\d+)$/.exec(CMD_DESC);
          if (match) {
            CMD_TYPE = 'AC_COOL';
            TEMPER = Number(match[1]);
          }
        }
      } else {
        TEMPER = (CMD_TYPE === 'AC_COOL' && state.modalCmdType.TEMPER) ? state.modalCmdType.TEMPER.value : null;
      }

      setState({ savingCmdType: true });
      await apiCall('/define-ircode-action', {
        devId: state.devId,
        // @ts-ignore
        IR_ID,
        // @ts-ignore
        CMD_TYPE,
        // @ts-ignore
        CMD_DESC,
        // @ts-ignore
        TEMPER,
      });
      const conflictingRows: { IR_ID: string }[] = [];
      for (const row of state.irList) {
        // @ts-ignore
        if (row.IR_ID === IR_ID) {
          // @ts-ignore
          row.CMD_TYPE = CMD_TYPE;
          // @ts-ignore
          row.CMD_DESC = CMD_DESC;
          // @ts-ignore
          row.TEMPER = TEMPER;
          continue;
        }
        if (isDut4()) {
          // DUT4: unicidade por CMD_DESC — ON_<temp> (por temperatura), Fan e Off (únicos)
          const isUniqueDesc = CMD_DESC === 'Fan' || CMD_DESC === 'Off' || /^ON_\d+$/.test(CMD_DESC ?? '');
          // @ts-ignore
          if (isUniqueDesc && row.CMD_DESC === CMD_DESC) {
            // @ts-ignore
            conflictingRows.push({ IR_ID: row.IR_ID });
            // @ts-ignore
            row.CMD_TYPE = null; row.CMD_DESC = null; row.TEMPER = null;
          }
        // @ts-ignore
        } else if (row.CMD_TYPE === CMD_TYPE) { row.CMD_TYPE = null; row.CMD_DESC = null; row.TEMPER = null; }
      }
      await Promise.all(conflictingRows.map((row) => apiCall('/define-ircode-action', {
        devId: state.devId,
        IR_ID: row.IR_ID,
        // @ts-ignore
        CMD_TYPE: null,
        // @ts-ignore
        CMD_DESC: null,
        // @ts-ignore
        TEMPER: null,
      })));
      state.showModal = null;
    } catch (err) { console.log(err); toast.error(t('erro')); }
    setState({ savingCmdType: false });
  };

  const emmitDutCommand = async (item) => {
    try {
      setState({ transmittingCommand: true });
      const result = await apiCall('/send-dut-aut-command', {
        devId: state.devId,
        IR_ID: item.IR_ID,
        // @ts-ignore
        CMD_TYPE: item.CMD_TYPE ?? null,
      });
      if (result.success !== true) {
        toast.warn(t('dutNaoRespondeuAoComando'));
      } else {
        toast.success(t('sucessoTransmitido'));
      }
    } catch (err) { console.log(err); toast.error(t('erro')); }
    setState({ transmittingCommand: false });
  };

  const sendCommandsTableToDut = async () => {
    try {
      setState({ savingCmdsTable: true });
      const result = await apiCall('/resend-dut-ir-codes', {
        devId: state.devId,
      });
      sessionStorage.clear();
      sessionStorage.setItem('savingOnDut', JSON.stringify(state.defaultIrList));
      setState({ haveError: false, modify: false });
      if (result.responses.some((r) => !r.success)) {
        toast.warn(t('dutNaoRespondeuAoComando'));
      } else {
        toast.success(t('sucessoTransmitido'));
      }
    } catch (err) { console.log(err); toast.error(t('erro')); setState({ haveError: true }); }
    setState({ savingCmdsTable: false });
  };
  const deleteIrCode = async (item) => {
    if (!window.confirm(t('desejaExcluirCodigo'))) {
      return;
    }
    try {
      await apiCall('/delete-dut-ircode', {
        devId: state.devId,
        IR_ID: item.IR_ID,
      });
      setState({ modify: true });
      toast.success(t('sucessoExcluir'));
      // @ts-ignore
      state.irList = state.irList.filter((x) => x.IR_ID !== item.IR_ID);
      render();
    } catch (err) { console.log(err); toast.error(t('erro')); }
  };

  async function fetchIRCommandSets(dutId: string) {
    if (!state.devInfo) return null;
    if (!state.devInfo.dut_aut) return null;

    const { list } = await apiCall('/get-duts-by-model', { devId: dutId });

    const probableCommands = list
      .filter((x) => x.DUT_ID !== state.devId && x.MCHN_MODEL)
      .map((dut) => ({
        ...dut,
        model: dut.MCHN_MODEL,
        distance: levenshtein.get(state.devInfo!.dut_aut!.MCHN_MODEL || '', dut.MCHN_MODEL || ''),
        dropdown_text: `${dut.MCHN_MODEL} - ${dut.DUT_ID}`,
      }));

    probableCommands.sort((a, b) => a.distance - b.distance);
    return probableCommands;
  }

  async function saveTableCmds() {
    setState({ savingCmdsTable: true });
    const commands = {
      configs: state.loadedIrList.map((x) => ({ IR_ID: x.IR_ID, CMD_TYPE: x.CMD_TYPE })),
      targetDutId: state.devId,
    };
    const response = await apiCall('/write-dut-command-by-ircode', commands);
    if (!response.success || response.success !== true) {
      toast.error(t('erro'));
      setState({ savingCmdsTable: false });
      return;
    }

    for (const config of state.loadedIrList) {
      const idx = state.irList.findIndex((x) => x.IR_ID === config.IR_ID);
      if (idx !== -1) {
        state.irList[idx] = config;
      }
      else {
        state.irList.push(config);
      }
    }

    for (const config of state.loadedIrList) {
      const idx = state.irList.findIndex((x) => x.IR_ID === config.IR_ID);
      if (idx !== -1) {
        state.irList[idx] = config;
      }
      else {
        state.irList.push(config);
      }

      for (const ir of state.irList) {
        if (config.CMD_TYPE === ir.CMD_TYPE && config.IR_ID !== ir.IR_ID) {
          // @ts-ignore
          ir.CMD_TYPE = null;
        }
      }
    }
    // let {list, dutCommandTypes} = await apiCall('/get-dut-ircodes-list', { devId: state.devId })
    // state.irList = list || state.irList
    // state.dutCommandTypes = dutCommandTypes || state.dutCommandTypes
    setState({ savingCmdsTable: false, modify: false });
  }

  function getCommandType(cmdDesc) {
    if (cmdDesc?.includes('ON') || cmdDesc?.includes('Set')) {
      return { name: t('refrigerar'), value: 'COOL' };
    }
    if (cmdDesc?.toUpperCase() === 'FAN') return { name: t('ventilar'), value: 'FAN' };
    if (cmdDesc?.toUpperCase() === 'OFF') return { name: t('desligar'), value: 'OFF' };
    if (cmdDesc) return { name: t('outro'), value: 'OTHER' };
    return null;
  }

  function getCommandTemp(temp: number | null | undefined, cmdDesc: string | null | undefined): { name: string; value: number } | null {
    if (temp) return { name: `${temp}°C`, value: Number(temp) };
    if (cmdDesc?.includes('ON')) {
      const cmdDescTemp = Number(cmdDesc.split('_')[1]) || null;
      return cmdDescTemp ? { name: `${cmdDescTemp}°C`, value: cmdDescTemp } : null;
    }
    return null;
  }

  function clearEditItemValues(item) {
    if (item.IR_ID) state.modalCmdType.IR_ID = item.IR_ID;
    state.modalCmdType.CMD_TYPE = getCommandType(item.CMD_DESC || '');
    if (item.CMD_DESC) state.modalCmdType.CMD_DESC = item.CMD_DESC;
    state.modalCmdType.CMD_ACTION = item.CMD_TYPE || '';
    state.modalCmdType.TEMPER = getCommandTemp(item.TEMPER, item.CMD_DESC);
  }

  function wantEditItem(item) {
    setState({ modify: true });
    clearEditItemValues(item);
    // @ts-ignore
    state.showModal = 'set-cmd-type';
    render();
  }

  function verifyParams() {
    if (!state.devInfo?.dut_aut || !state.devInfo?.CLIENT_ID || !state.devInfo?.UNIT_ID) {
      return true;
    }
    return false;
  }

  async function saveModel() {
    try {
      setState({ savingModel: true });
      if (verifyParams()) {
        toast.error(t('houveErro'));
        return;
      }
      await apiCall('/dut/set-dut-info', {
        DEV_ID: state.devId,
        MCHN_BRAND: state.MCHN_BRAND_item?.value,
        MCHN_MODEL: state.MCHN_MODEL,
        CLIENT_ID: state.devInfo?.CLIENT_ID,
        UNIT_ID: state.devInfo?.UNIT_ID,
        groups: state.devInfo?.dut_aut?.groups?.map((item) => item.GROUP_ID.toString()),
      });
      toast.success(t('salvoSucesso'));
      render();
    } catch (err) {
      toast.error(t('houveErro'));
    }
    setState({ savingModel: false });
    render();
  }

  const deleteAllSelectedCommands = async () => {
    try {
      setState({ deletingSelectedCmds: true });
      // @ts-ignore
      const checkedItems = state.irList.filter((ir) => ir.checked);
      await Promise.all(checkedItems.map((ir) => apiCall('/delete-dut-ircode', {
        devId: state.devId,
        IR_ID: ir.IR_ID,
      })));
      // @ts-ignore
      setState({ irList: state.irList.filter((ir) => !ir.checked) });
      setState({ modify: true });
      render();
      toast.success(t('sucessoTransmitido'));
    } catch (err) { console.log(err); toast.error(t('erro')); }
    // @ts-ignore
    setState({ selectedDutCommands: state.irList.filter((x) => x.checked).length });
    setState({ deletingSelectedCmds: false });
  };

  function verifyPermissions(devInfo: DevFullInfo): boolean {
    const permissionManageClient = !!devInfo?.CLIENT_ID
      && profile.permissions.CLIENT_MANAGE.includes(devInfo.CLIENT_ID);

    const hasPermission = profile.manageAllClients
      || profile.permissions.isUserManut
      || profile.permissions.isInstaller
      || permissionManageClient;

    return hasPermission;
  }

  function calcSelectPosition(clientX: number, clientY: number) {
    const containerWidth = 288;
    const containerHeight = 60;
    const margin = 8;
    const rawLeft = clientX + margin;
    const left = rawLeft + containerWidth + margin > window.innerWidth
      ? window.innerWidth - containerWidth - margin
      : rawLeft;
    const top = clientY + containerHeight + margin > window.innerHeight
      ? clientY - containerHeight - margin
      : clientY + margin;
    return { left, top };
  }

  function buildScreenInfoParams(): Parameters<(typeof AssetLayout)>[0]['screenInfo'] {
    return {
      forceHideHealthTab: false,
      groupSelected: true,
      hasNonDutDeviceInAssets: false,
      dutAutomationInfo: {},
      isDuoSelected: state.devInfo?.dut?.PLACEMENT === 'DUO',
      assetRoleSelected: 0,
    };
  }

  async function onChangeLearnActive() {
    if (state.devInfo.FW_VERSION && isFwVersionGreatestOrEqual('7.0.0', state.devInfo.FW_VERSION)) {
      const newEnabled = !state.learnActiveAwait;
      setState({ isLoadingIrLearning: true, learnActiveAwait: newEnabled });
      if (state.wsConn) {
        state.wsConn.send({
          type: 'dutSubscribeIrReadAndWait', data: { DUT_ID: state.devId, enabled: newEnabled },
        });
      }
      irLearningTimeoutRef.current = setTimeout(() => {
        setState({ isLoadingIrLearning: false, learnActiveAwait: false, learnActiveBox: false });
        toast.error(t('erroNaComunicacaoComODispositivo'));
      }, 6000);
    } else {
      setState({ learnActive: !state.learnActive, learnActiveBox: !state.learnActiveBox });
    }
  }

  return (
    <>
      <RouterPrompt
        when={state.modify}
        title=""
        cancelText={t('sair')}
        okText={t('botaoSalvar')}
        onOK={() => { setState({ modify: false }); return true; }}
        onCancel={async () => { await sendCommandsTableToDut(); if (!state.haveError) { setState({ modify: false }); return true; } return false; }}
      >
        <p>{t('comandosNaoForamSalvosDutDesejaContinuar')}</p>
      </RouterPrompt>
      <Helmet>
        <title>{generateNameFormatted(state.devInfo?.DEV_ID, t('controleRemoto'))}</title>
      </Helmet>
      {!state.assetLayout ? (<DevLayout devInfo={state.devInfo} />) : (<AssetLayout devInfo={state.devInfo} screenInfo={buildScreenInfoParams()} />)}
      {(state.loading) ? <Loader />
        : (
          <>
            <Card style={{ marginTop: '30px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '30px' }}>
                {state.devInfo?.dut_aut.MCHN_MODEL && (
                  <div>
                    <h4>
                      {t('modeloMaquina')}
                      :
                      {' '}
                      {state.devInfo?.dut_aut?.MCHN_MODEL}
                    </h4>
                  </div>
                )}
                <div>&nbsp;</div>
                <div>{(state.transmittingCommand) && <Loader />}</div>
                <div style={{
                  display: 'flex',
                  maxWidth: '360px',
                  width: '100%',
                }}
                >
                  {(state.permissionGeral) && (
                    <Button style={{ width: '360px' }} onClick={sendCommandsTableToDut} variant="primary" disabled={state.savingCmdsTable}>
                      {state.savingCmdsTable
                        ? <Loader size="small" />
                        : t('botaoSalvarComandosDut')}
                    </Button>
                  )}
                </div>
              </div>
              {(state.permissionGeral) && (
                <div style={{
                  display: 'flex', marginBottom: '15px', gap: 10, alignItems: 'center',
                }}
                >
                  <Checkbox
                    checked={state.learnActiveBox}
                    onClick={onChangeLearnActive}
                    color="primary"
                  />
                  <span>{t('habilitarAprendizagem')}</span>
                </div>
              )}
              {(state.permissionGeral) && (
                <div style={{ display: 'flex' }}>
                  <Flex flexWrap="wrap" width="750px" pt={24} mb={24} justifyContent="space-between">
                    {
                      profile.permissions.isInstaller && (
                      <div style={{
                        padding: '5px',
                        width: '100%',
                        marginBottom: '10px',
                        borderRadius: '5px',
                        gap: '5px',
                        backgroundColor: '#dddddd46',
                      }}
                      >
                        <h3 onClick={() => setState({ openEdit: !state.openEdit })}>
                          <b>
                            { state.openEdit ? <MdArrowDropDown /> : <MdArrowRight /> }
                            {' '}
                            {t('editarAcrescentarModelo')}
                          </b>
                        </h3>
                        {
                              state.openEdit && (
                                <>
                                  <CustomSelect
                                    options={state.comboOpts.brands || []}
                                    value={state.MCHN_BRAND_item}
                                    placeholder={t('marca')}
                                    onSelect={(item) => { setState({ MCHN_BRAND_item: item }); render(); }}
                                  />
                                  <TextLine style={{ marginBottom: '10px' }}>
                                    <Input
                                      type="text"
                                      value={state.MCHN_MODEL}
                                      label={t('modelo')}
                                      onChange={(event) => { setState({ MCHN_MODEL: event.target.value }); }}
                                    />
                                  </TextLine>
                                  <Button style={{ width: '120px', marginBottom: '20px' }} onClick={saveModel} variant="primary" disabled={state.savingCmdsTable}>
                                    {state.savingModel
                                      ? <Loader size="small" variant="secondary" />
                                      : t('botaoSalvar')}
                                  </Button>
                                </>
                              )
                            }
                      </div>
                      )
                    }
                    <Box minWidth="250px" width={[3 / 5]} mb={[16]}>
                      {profile.permissions.isInstaller && (
                      <label>
                        Selecionar modelo:
                      </label>
                      )}
                      <Select
                        haveFuzzySearch
                        options={(state.probableIrCommands || []).sort((a, b) => a.distance - b.distance)}
                        placeholder={t('modelos')}
                        propLabel="dropdown_text"
                        value={state.loadedIrModel}
                        onSelect={async (ir_command) => {
                          if (ir_command === '') {
                            setState({ loadedIrList: state.defaultIrList, loadedIrModel: '' });
                            render();
                            return;
                          }

                          try {
                            const { list } = await apiCall('/get-dut-ircodes-list', { devId: ir_command.DUT_ID });
                            const clonedIrCodes = JSON.parse(JSON.stringify(list || []));
                            setState({ loadedIrList: clonedIrCodes, loadedIrModel: ir_command.dropdown_text });
                          } catch (err) {
                            toast.error(t('erro'));
                            console.error(err);
                          }
                          render();
                        }}
                      />
                    </Box>
                    <Box minWidth={[1 / 5]} mb={[16]}>
                      <Button style={{ width: '120px' }} onClick={saveTableCmds} variant="primary" disabled={state.savingCmdsTable}>
                        {state.savingCmdsTable
                          ? <Loader size="small" variant="secondary" />
                          : t('botaoSalvarConfig')}
                      </Button>
                    </Box>
                  </Flex>
                </div>
              )}
              <IrTableContainer>
                <IrTableHead>
                  <tr>
                    {state.permissionGeral && (
                      <IrHeaderCell style={{ width: 48, textAlign: 'center' }}>
                        <Checkbox
                          key="all_checked"
                          // @ts-ignore
                          checked={state.irList.every((item) => !!item.checked) && state.irList.length > 0}
                          onClick={(event) => {
                            // @ts-ignore
                            state.checkedAllCodes = !state.checkedAllCodes;
                            // @ts-ignore
                            state.irList.forEach((item) => { item.checked = state.checkedAllCodes; });
                            const pos = calcSelectPosition(event.clientX, event.clientY);
                            state.selectPositionX = pos.left;
                            state.selectPositionY = pos.top;
                            // @ts-ignore
                            setState({ selectedDutCommands: state.irList.filter((x) => x.checked).length });
                            render();
                          }}
                        />
                      </IrHeaderCell>
                    )}
                    {!isDut4() && state.permissionGeral && (
                      <IrHeaderCell style={{ width: 260 }}>{t('codigo')}</IrHeaderCell>
                    )}
                    <IrHeaderCell>{t('comentario')}</IrHeaderCell>
                    {isDut4() && <IrHeaderCell style={{ width: 120 }}>{t('tipo')}</IrHeaderCell>}
                    <IrHeaderCell style={{ width: isDut4() ? 160 : 200 }}>{t('acaoAutomocao')}</IrHeaderCell>
                  </tr>
                </IrTableHead>
                <IrTableBody>
                  {!state.savingCmdsTable && state.irList.map((item) => (
                    // @ts-ignore
                    <IrRow key={item.IR_ID}>
                      {state.permissionGeral && (
                        // @ts-ignore
                        <IrDataCell style={{ textAlign: 'center' }}>
                          <Checkbox
                            // @ts-ignore
                            key={`check:${item.IR_ID}`}
                            // @ts-ignore
                            checked={item.checked}
                            // @ts-ignore
                            onClick={(event) => {
                              // @ts-ignore
                              item.checked = !item.checked;
                              const pos = calcSelectPosition(event.clientX, event.clientY);
                              state.selectPositionX = pos.left;
                              state.selectPositionY = pos.top;
                              // @ts-ignore
                              setState({ selectedDutCommands: state.irList.filter((x) => x.checked).length });
                              render();
                            }}
                          />
                        </IrDataCell>
                      )}
                      {!isDut4() && state.permissionGeral && (
                        // @ts-ignore
                        <IrDataCell>{item.IR_ID}</IrDataCell>
                      )}
                      {/* @ts-ignore */}
                      <IrDataCell>
                        <span>{item.CMD_DESC}</span>
                        {/* @ts-ignore */}
                        {!isDut4() && item.TEMPER != null && ` [${item.TEMPER}]`}
                      </IrDataCell>
                      {isDut4() && (
                        // @ts-ignore
                        <IrDataCell>
                          {/* @ts-ignore */}
                          {item.TEMPER != null && (
                            <TempBadge>
                              {item.TEMPER}
                              °C
                            </TempBadge>
                          )}
                          {/* @ts-ignore */}
                          {item.CMD_DESC === 'Fan' && <FanBadge>{t('ventilar')}</FanBadge>}
                          {/* @ts-ignore */}
                          {item.CMD_DESC === 'Off' && <OffBadge>{t('desligar')}</OffBadge>}
                        </IrDataCell>
                      )}
                      {/* @ts-ignore */}
                      <IrDataCell>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          {/* @ts-ignore */}
                          {!isDut4() && <span>{item.CMD_TYPE}</span>}
                          <div>
                            {state.permissionGeral && (
                              <ActionButton data-testid="ir-command-edit-btn" onClick={() => wantEditItem(item)} variant="blue-inv">
                                <EditIcon color={colors.LightBlue} />
                              </ActionButton>
                            )}
                            <ActionButton data-testid="ir-command-send-btn" onClick={() => emmitDutCommand(item)} variant="blue-inv">
                              <img style={{ width: '20px' }} src={TransmitIcon} />
                            </ActionButton>
                            {state.permissionGeral && (
                              <ActionButton data-testid="ir-command-delete-btn" onClick={() => deleteIrCode(item)} variant="red-inv">
                                <DeleteOutlineIcon colors={colors.Red} />
                              </ActionButton>
                            )}
                          </div>
                        </div>
                      </IrDataCell>
                    </IrRow>
                  ))}
                </IrTableBody>
              </IrTableContainer>
              {state.selectedDutCommands > 0 && (
                <SelectContainer style={{ width: '280px', top: state.selectPositionY, left: state.selectPositionX }}>
                  <Select
                    options={[{ label: t('apagar'), value: 'erase' }]}
                    value={null}
                    placeholder={`${state.selectedDutCommands} selecionada(s)`}
                    onSelect={async (opt) => {
                      if (opt.value === 'erase') {
                        await deleteAllSelectedCommands();
                      }
                    }}
                  />
                </SelectContainer>
              )}
            </Card>
          </>
        )}
      {
        (state.showModal === 'set-cmd-type')
        && (
          <ModalWindow onClickOutside={() => null} style={isDut4() ? { minWidth: '500px', overflow: 'visible' } : undefined}>
            <div style={{ marginBottom: '20px' }}>
              <Select
                data-testid="cmd-type-select"
                options={state.dutCommandTypes}
                propLabel="name"
                value={state.modalCmdType.CMD_TYPE}
                placeholder={t('tipoComando')}
                onSelect={(item) => { clearEditItemValues({}); state.modalCmdType.CMD_TYPE = item; render(); }}
              />
            </div>
            {state.modalCmdType.CMD_TYPE?.value === 'COOL' && (
              <div style={{ marginBottom: '20px' }}>
                <Select
                  options={state.dutDefaultTemps}
                  propLabel="name"
                  value={state.modalCmdType.TEMPER}
                  placeholder={t('Temperatura')}
                  onSelect={(item) => { state.modalCmdType.TEMPER = item; render(); }}
                />
              </div>
            )}
            {state.modalCmdType.CMD_TYPE?.value === 'OTHER' && (
              <div style={{ marginBottom: '20px' }}>
                <Input
                  type="text"
                  // @ts-ignore
                  value={state.modalCmdType.CMD_DESC}
                  placeholder={t('comentario')}
                  onChange={(event) => {
                    state.modalCmdType.CMD_DESC = event.target.value;
                    if (isDut4() && /^ON_\d+$/.test(event.target.value)) {
                      state.modalCmdType.CMD_ACTION = 'AC_COOL';
                    }
                    render();
                  }}
                />
              </div>
            )}
            {!isDut4() && (
              <div style={{ marginBottom: '20px' }}>
                <Select
                  options={state.dutCommandActions}
                  value={state.modalCmdType.CMD_ACTION}
                  placeholder={t('acaoAutomocao')}
                  onSelect={(item) => { state.modalCmdType.CMD_ACTION = item; render(); }}
                />
              </div>
            )}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '30px',
            }}
            >
              <Button style={{ width: '120px' }} onClick={confirmSetCmdType} variant="primary" disabled={state.savingCmdType}>
                {state.savingCmdType
                  ? <Loader size="small" />
                  : t('botaoSalvar')}
              </Button>
              {/* @ts-ignore */}
              <Button style={{ width: '120px', margin: '0 20px' }} onClick={() => setState({ showModal: null })} variant="grey">
                {t('botaoCancelar')}
              </Button>
            </div>
          </ModalWindow>
        )
      }
      {
        state.isLoadingIrLearning && (
          <Overlay>
            <Loader variant="primary" />
            <TextLine style={{ color: colors.White, marginTop: '20px' }}>
              {t('aguardandoRespostaDoDispositivo')}
            </TextLine>
          </Overlay>
        )
      }
    </>
  );
};

export default withTransaction('DutIrManagement', 'component')(DutIrManagement);
